// Channel (newsletter) name resolution for the recipient picker.
//
// #164: channels rendered their raw newsletter JID with a leading "+" (e.g.
// "+120363425980016518"), because `getWaContactDisplayLabel` ends its fallback
// chain at `extractPhoneNumber(id)` — which, for a `@newsletter` id, fakes a
// phone number. Meanwhile the channel's real name is available in the user's own
// scheduled messages (`usermessages.contactList[].name`). This module harvests
// that name and resolves the label at the picker, and never lets a newsletter id
// render as a phone number.
//
// Pure (no React/redux) so it is unit-testable in the repo's node jest env.

import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';

function serializedIdOf(contact: any): string {
  return (
    contact?.id?._serialized ||
    (typeof contact?.id === 'string' ? contact.id : '') ||
    contact?.waId ||
    contact?._serialized ||
    contact?.whatsappId ||
    contact?._id ||
    ''
  );
}

/**
 * A channel name that is real: present, non-empty, and not just the JID (or its
 * numeric part) echoed back as the "name". Returns undefined otherwise.
 */
function cleanChannelName(name: unknown, id: string): string | undefined {
  if (typeof name !== 'string') return undefined;
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  if (trimmed === id) return undefined; // whole JID echoed
  if (trimmed === id.split('@')[0]) return undefined; // numeric part echoed
  return trimmed;
}

/**
 * Harvest a `newsletterId -> channel name` index from the user's scheduled
 * messages. Each `contactList` entry for a channel carries the name that was
 * resolved when the message was created, so this recovers names WhatsApp no
 * longer surfaces in the live store. Keeps the first real name seen per id.
 */
export function buildChannelNameIndex(
  userMessages: ReadonlyArray<any> | undefined | null,
): Map<string, string> {
  const index = new Map<string, string>();
  for (const message of userMessages ?? []) {
    const contactList = message?.contactList;
    if (!Array.isArray(contactList)) continue;
    for (const contact of contactList) {
      const id = contact?.id;
      if (typeof id !== 'string' || chatKindOf(id) !== 'channel' || index.has(id)) continue;
      const name =
        cleanChannelName(contact?.name, id) ?? cleanChannelName(contact?.formattedTitle, id);
      if (name) index.set(id, name);
    }
  }
  return index;
}

/** Real channel name off the contact's own fields — no phone-number fallback. */
function channelNameFromContact(contact: any, id: string): string | undefined {
  return (
    cleanChannelName(contact?.formattedTitle, id) ??
    cleanChannelName(contact?.name, id) ??
    cleanChannelName(contact?.displayName, id) ??
    cleanChannelName(contact?.verifiedName, id) ??
    cleanChannelName(contact?.pushname, id)
  );
}

/**
 * Display label for a picker row.
 *
 * For a channel (`@newsletter`): the real name off the contact, else the name
 * harvested from the user's scheduled messages, else a neutral placeholder —
 * NEVER the JID rendered as a fake phone number (#164).
 *
 * For everything else: `fallbackLabel` (the caller's normal
 * `getWaContactDisplayLabel` result) is returned unchanged, so contacts and
 * groups are entirely unaffected.
 */
export function resolveContactLabel(
  contact: any,
  channelNameIndex: Map<string, string>,
  fallbackLabel: string,
  channelPlaceholder: string,
): string {
  const id = serializedIdOf(contact);
  if (chatKindOf(id) !== 'channel') return fallbackLabel;
  return (
    channelNameFromContact(contact, id) ??
    channelNameIndex.get(id) ??
    channelPlaceholder
  );
}
